import { Form } from "formik";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";

import { admin_cse } from "@/config/roles";
import { Button, ButtonSizes, ButtonTypes, Checkbox, CustomForm } from "@/component-library";
import { Button2 } from "@/ui2/atoms/Button";
import { carePlanRequests } from "@/api/(requests)/carePlan";
import { CharacterCounter } from "@/component-library/CharacterCounter/CharacterCounter";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import EditIcon from "@/assets/images/icons2/icon--edit.svg?component";
import { FormFieldError } from "@/component-library/FormFields/FormFieldError/FormFieldError";
import { FormTextInput } from "@/component-library/FormFields/FormTextInput/FormTextInput";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { hideLoader, showLoader } from "@/redux/actions/loader";
import { isAuthorized } from "@/utils/authToken";
import { localTimestampFromStandardTimestamp } from "@/utils/momentUtils";
import { Modal2 } from "@/ui2/molecules/Modal";
import { ProviderEnum } from "@/core/constants/provider";
import { showToast } from "@/providers/ToastProvider";
import { toastMessageTypes } from "@/redux/actions/toaster";
import { XSS_REGEX } from "@/constants/security";

import "./PatientStrengths.styles.scss";

const DESCRIPTION_TEXT = "What positive qualities does the patient possess?";
const OTHER_OPTION_ID = "S.OTHER";

export function PatientStrengths(props) {
  const profile = useSelector((state) => state.auth.profile);
  const { existingStrengths, patientStrengthOptions, refreshStrengths } = props;
  const dispatch = useDispatch();

  const onModalSubmission = async (patientStrengths, otherFreeText, close) => {
    dispatch(showLoader());
    try {
      await carePlanRequests.update_patient_strengths({
        context: {
          exceptionConfig: {
            CarePlanInvalidVersionException: {
              toastDismissTimeout: 300000, // 5 minutes in millis
              toastErrorType: toastMessageTypes.error_v2,
            },
          },
          showLoader: false,
        },
        data: {
          current_version: existingStrengths.current_version,
          patient_strengths: patientStrengths,
          patient_strengths_other_free_text: otherFreeText,
        },
        urlParams: { patientId: props.patientId },
      });

      await refreshStrengths();
      close();

      showToast([{ heading: "Patient strengths updated.", variant: "success" }]);
    } finally {
      dispatch(hideLoader());
    }
  };

  const renderExistingPatientStrengths = () => {
    const {
      patient_strengths,
      patient_strengths_other_free_text,
      updated_by_name,
      updated_timestamp,
    } = existingStrengths;

    const formattedStrengths = patient_strengths
      .map((strengthId) => {
        const strengthOption = patientStrengthOptions.find(
          (option) => option.strength_id === strengthId,
        );

        if (strengthOption.strength_id === OTHER_OPTION_ID) {
          return patient_strengths_other_free_text;
        }

        return strengthOption.strength_text;
      })
      .join(", ");

    return (
      <>
        <div
          className="tw:overflow-auto"
          {...getAutomationDataAttr("PatientStrengths_existingStrengths")}
        >
          <p className="PatientGoalsStrengthsCautions--readonly-text">{formattedStrengths}</p>
        </div>

        <small className="tw:flex tw:justify-between tw:text-md tw:text-text-tertiary">
          <span>Last Updated by {updated_by_name}</span>

          <span>{localTimestampFromStandardTimestamp(updated_timestamp, "M/D/YYYY")}</span>
        </small>
      </>
    );
  };

  const renderNoPatientStrengthsSpecified = () => {
    const isAuthorizedToEditStrengths = isAuthorizedToEdit();
    const noPermissionText =
      "Authorized members of the patient's aptihealth care team can update patient strengths.";
    const hasPermissionText = 'Choose "Add" to add strengths.';
    const textBackgroundClass = `PatientGoalsStrengthsCautions--empty${
      isAuthorizedToEditStrengths ? "" : " no-permission"
    }`;

    return (
      <div {...getAutomationDataAttr("PatientStrengths_emptyStrengths")}>
        <div className={textBackgroundClass}>
          <p className="PatientGoalsStrengthsCautions--empty-text">
            {isAuthorizedToEditStrengths ? hasPermissionText : noPermissionText}
          </p>
        </div>
      </div>
    );
  };

  const isAuthorizedToEdit = () => {
    if (isAuthorized(admin_cse)) {
      return profile?.is_edit_care_plan_enabled;
    } else {
      return (
        props.isProviderOnCareTeam &&
        profile?.provider_type &&
        [
          ProviderEnum.BEHAVIORAL,
          ProviderEnum.CASE_MANAGER,
          ProviderEnum.PEER,
          ProviderEnum.PRESCRIBE,
        ].includes(profile.provider_type)
      );
    }
  };

  const editLabel = existingStrengths?.patient_strengths?.length ? "Edit" : "Add";

  return (
    <DashboardCard2
      Actions={
        isAuthorizedToEdit() && (
          <Modal2
            overlayProps={{ isDismissable: true }}
            title={`${editLabel} Patient Strengths`}
            trigger={
              <Button2
                icon={<EditIcon />}
                size="sm"
                variant="link"
                {...getAutomationDataAttr("PatientStrengths_editButton")}
              >
                {editLabel}
              </Button2>
            }
          >
            {({ close }) => (
              <PatientStrengthsForm
                existingStrengths={existingStrengths}
                onClose={close}
                onModalSubmission={(strengths, otherText) =>
                  onModalSubmission(strengths, otherText, close)
                }
                patientStrengthOptions={patientStrengthOptions}
              />
            )}
          </Modal2>
        )
      }
      className="PatientGoalsStrengthsCautions--element-container"
      classNameChildren="tw:max-h-80 tw:gap-3 tw:grid tw:grid-rows-[auto_1fr_auto]"
      title="Patient Strengths"
      {...getAutomationDataAttr("PatientStrengths_container")}
    >
      <small className="tw:text-sm tw:text-text-tertiary">{DESCRIPTION_TEXT}</small>

      {existingStrengths?.patient_strengths?.length
        ? renderExistingPatientStrengths()
        : renderNoPatientStrengthsSpecified()}
    </DashboardCard2>
  );
}

const PatientStrengthsForm = (props) => {
  const { existingStrengths, patientStrengthOptions } = props;
  const {
    patient_strengths: initialPatientStrengths,
    patient_strengths_other_free_text: initialOtherFreeText,
  } = existingStrengths;

  const maxOtherTextCharacters = 280;
  const patientStrengthsSchema = yup.object().shape({
    otherFreeText: yup.string().when("patientStrengths", (strengths, schema) => {
      return strengths.includes(OTHER_OPTION_ID)
        ? yup
            .string()
            .required("Other reason must be specified")
            .max(maxOtherTextCharacters, `Limit text to ${maxOtherTextCharacters} characters`)
            .test("special characters", "Special characters are not allowed", (value) => {
              return !XSS_REGEX.test(value);
            })
        : schema;
    }),
    patientStrengths: yup.array().of(yup.string()),
  });

  const checkboxHandler = async (formikProps, selectedOption) => {
    const updatedPatientStrengths = new Set(formikProps.values.patientStrengths);
    let updatedOtherFreeText = formikProps.values.otherFreeText;

    if (updatedPatientStrengths.has(selectedOption.strength_id)) {
      updatedPatientStrengths.delete(selectedOption.strength_id);

      // Reset the other text data
      if (selectedOption.strength_id === OTHER_OPTION_ID) {
        updatedOtherFreeText = "";
      }
    } else {
      updatedPatientStrengths.add(selectedOption.strength_id);
    }

    await formikProps.setValues({
      otherFreeText: updatedOtherFreeText,
      patientStrengths: Array.from(updatedPatientStrengths),
    });
  };

  const renderOtherTextInput = (formikProps, formikInputProps) => {
    return (
      <>
        <FormTextInput
          {...formikInputProps}
          formikProps={formikProps}
          name="otherFreeText"
          readOnly={!formikProps?.values?.patientStrengths?.includes(OTHER_OPTION_ID)}
          testId="PatientStrengths_otherTextInput"
          triggerErrorOnChange
        />
        <CharacterCounter
          characterLimit={maxOtherTextCharacters}
          className="PatientGoalsStrengthsCautions--input_character_counter"
          length={formikProps.values?.otherFreeText?.length}
        />
      </>
    );
  };

  const renderOptions = (formikProps) => {
    const formikInputProps = {
      errors: formikProps.errors,
      inputClasses: "Input--sm",
      touched: formikProps.touched,
    };

    const options = patientStrengthOptions.map((option, index) => {
      return (
        <div key={index}>
          <Checkbox
            checked={formikProps.values.patientStrengths.includes(option.strength_id)}
            label={option.strength_text}
            name="patientStrengths"
            onClick={async () => checkboxHandler(formikProps, option)}
            testId={`PatientStrengths_input_${option.strength_id}`}
            value={option.strength_id}
          />
          {option.strength_id === OTHER_OPTION_ID &&
            renderOtherTextInput(formikProps, formikInputProps)}
        </div>
      );
    });

    const splitIndex = Math.ceil(options.length / 2);

    return (
      <>
        <div className="PatientStrengths--modal-options">{options.slice(0, splitIndex)}</div>
        <div className="PatientStrengths--modal-options">
          {options.slice(splitIndex, options.length)}
        </div>
      </>
    );
  };

  const renderForm = (formikProps) => {
    return (
      <Form>
        <div className="PatientGoalsStrengthsCautions--modal_form">
          <div className="FormField-header_container" data-public>
            <p className="PatientGoalsStrengthsCautions--modal_subtitle">{DESCRIPTION_TEXT}</p>
          </div>

          <div className="PatientStrengths--modal-option-container">
            {renderOptions(formikProps)}
          </div>
          {formikProps.errors?.patientStrengths && (
            <FormFieldError>{formikProps.errors.patientStrengths}</FormFieldError>
          )}

          <div className="PatientGoalsStrengthsCautions-button_wrapper">
            <Button
              buttonSize={ButtonSizes.medium}
              buttonType={ButtonTypes.primaryV2}
              type="submit"
            >
              Save
            </Button>
            <Button
              buttonSize={ButtonSizes.medium}
              buttonType={ButtonTypes.primaryOutlineV2}
              onClick={props.onClose}
              type="button"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Form>
    );
  };

  return (
    <CustomForm
      initialValues={{
        otherFreeText: initialOtherFreeText ?? "",
        patientStrengths: [...initialPatientStrengths],
      }}
      onSubmit={async (formData) => {
        await props.onModalSubmission(formData.patientStrengths, formData.otherFreeText);
      }}
      render={renderForm}
      validateOnBlur={true}
      validateOnChange={true}
      validationSchema={patientStrengthsSchema}
    />
  );
};
